import React, { useEffect, useState } from "react";
import Tournament from "./pages/Tournament.tsx";
import Championship from "./pages/Championship.tsx";
import Players from "./pages/Players.tsx";
import Configuration from "./pages/Configuration.tsx";
import { getAllPlayers } from "./utils/firebase-utils";
import { HandicapSettings, Player, RankingMode, Result } from "./types.ts";
import {
  DEFAULT_HANDICAP_SETTINGS,
  LEGACY_HANDICAP_MIN,
} from "./utils/tournament-calc.ts";
import { resolveRankingMode } from "./utils/ranking.ts";
import { getTodayCzech } from "./utils/date-utils.ts";
import { useAuth } from "./context/AuthContext.tsx";
import ChessLoginScreen from "./components/ChessLoginScreen.tsx";
import Header from "./components/Header.tsx";
import PublicSite from "./pages/PublicSite.tsx";
import { Navigate, useLocation } from "react-router-dom";

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("tournament");
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  // Úvodní stránka je veřejná a přístupná i bez přihlášení.
  // "/public" zůstává funkční kvůli dříve sdíleným odkazům.
  const isPublicView =
    location.pathname === "/" || location.pathname === "/public";
  const isLoginView = location.pathname === "/login";
  // Načtení dat z localStorage nebo použití výchozích hodnot
  const [playerCount, setPlayerCount] = useState<number>(() => {
    const savedCount = localStorage.getItem("playerCount");
    return savedCount ? parseInt(savedCount, 10) : 11;
  });

  const [players, setPlayers] = useState<(Player | null)[]>(() => {
    const savedPlayers = localStorage.getItem("players");
    return savedPlayers
      ? JSON.parse(savedPlayers)
      : Array(playerCount).fill(null);
  });

  const [results, setResults] = useState<Result[][]>(() => {
    const savedResults = localStorage.getItem("results");
    if (savedResults) {
      return JSON.parse(savedResults);
    }
    return [];
  });

  const [allPlayers, setAllPlayers] = useState<Player[]>([]);

  // Dvoukolový turnaj – každá dvojice hraje dvakrát (bílými i černými)
  const [doubleRound, setDoubleRound] = useState<boolean>(
    () => localStorage.getItem("doubleRound") === "true"
  );

  // Datum turnaje – výchozí je dnešek, jde ale přepsat (výsledky se dají
  // zadat i o den později)
  const [tournamentDate, setTournamentDate] = useState<string>(
    () => localStorage.getItem("tournamentDate") || getTodayCzech()
  );

  // Režim hodnocení pořadí při rovnosti bodů (Kavárna / FIDE / Volitelný)
  const [rankingMode, setRankingMode] = useState<RankingMode>(() =>
    resolveRankingMode(localStorage.getItem("rankingMode"))
  );

  // Globální nastavení přepočtu časů po turnaji (meze a řešení kolize pravidel)
  const [handicapSettings, setHandicapSettings] = useState<HandicapSettings>(
    () => {
      const saved = localStorage.getItem("handicapSettings");
      if (saved) {
        try {
          const parsed = { ...DEFAULT_HANDICAP_SETTINGS, ...JSON.parse(saved) };
          // Minimum se změnilo z 1:30 na 2:00. Kdo měl uloženou starou
          // výchozí hodnotu, dostane novou; vlastní nastavení zůstává.
          if (parsed.min === LEGACY_HANDICAP_MIN) {
            parsed.min = DEFAULT_HANDICAP_SETTINGS.min;
          }
          return parsed;
        } catch (e) {
          console.error("Chyba při načítání nastavení časů:", e);
        }
      }
      return DEFAULT_HANDICAP_SETTINGS;
    }
  );

  // Inicializace results při prvním načtení
  React.useEffect(() => {
    if (results.length === 0) {
      const newResults: Result[][] = [];
      for (let i = 0; i < playerCount; i++) {
        newResults[i] = [];
        for (let j = 0; j < playerCount; j++) {
          newResults[i][j] = { value: null, editable: true };
          if (i === j) {
            newResults[i][j] = { value: i + 1, editable: false };
          }
        }
      }
      setResults(newResults);
    }
  }, [results, playerCount]);

  useEffect(() => {
    // Načtení hráčů při prvním vykreslení
    const fetchPlayers = async () => {
      try {
        const players = (await getAllPlayers()) as Player[];
        console.log("Načtení hráčů z Firebase:", players);
        setAllPlayers(players);
      } catch (err) {
        console.error("Chyba při načítání hráčů:", err);
      }
    };

    fetchPlayers();
  }, []);

  // Ukládání dat do localStorage při každé změně
  useEffect(() => {
    localStorage.setItem("playerCount", playerCount.toString());
  }, [playerCount]);

  useEffect(() => {
    localStorage.setItem("players", JSON.stringify(players));
  }, [players]);

  useEffect(() => {
    localStorage.setItem("results", JSON.stringify(results));
  }, [results]);

  useEffect(() => {
    localStorage.setItem("allPlayers", JSON.stringify(allPlayers));
  }, [allPlayers]);

  useEffect(() => {
    localStorage.setItem("handicapSettings", JSON.stringify(handicapSettings));
  }, [handicapSettings]);

  useEffect(() => {
    localStorage.setItem("tournamentDate", tournamentDate);
  }, [tournamentDate]);

  useEffect(() => {
    localStorage.setItem("rankingMode", rankingMode);
  }, [rankingMode]);

  useEffect(() => {
    localStorage.setItem("doubleRound", doubleRound.toString());
  }, [doubleRound]);

  // Funkce pro manuální export a import dat
  const exportData = () => {
    const data = {
      playerCount,
      players,
      results,
      allPlayers,
      handicapSettings,
      tournamentDate,
      rankingMode,
    };

    const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `chess-tournament-backup-${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importData = (jsonData: string) => {
    try {
      const data = JSON.parse(jsonData);

      if (data.playerCount) setPlayerCount(data.playerCount);
      if (data.players) setPlayers(data.players);
      if (data.results) setResults(data.results);
      if (data.allPlayers) setAllPlayers(data.allPlayers);
      if (data.handicapSettings) {
        setHandicapSettings({
          ...DEFAULT_HANDICAP_SETTINGS,
          ...data.handicapSettings,
        });
      }
      if (data.tournamentDate) setTournamentDate(data.tournamentDate);
      if (data.rankingMode) setRankingMode(resolveRankingMode(data.rankingMode));

      // Uložení importovaných dat do localStorage
      localStorage.setItem("playerCount", data.playerCount.toString());
      localStorage.setItem("players", JSON.stringify(data.players));
      localStorage.setItem("results", JSON.stringify(data.results));
      localStorage.setItem("allPlayers", JSON.stringify(data.allPlayers));

      alert("Data byla úspěšně importována.");
    } catch (error) {
      console.error("Chyba při importu dat:", error);
      alert("Došlo k chybě při importu dat. Zkontrolujte formát souboru.");
    }
  };

  const tabs = [
    { id: "tournament", label: "Aktuální turnaj", component: Tournament },
    { id: "championship", label: "Výsledky turnajů", component: Championship },
    { id: "players", label: "Hráči", component: Players },
    { id: "configuration", label: "Konfigurace", component: Configuration },
  ];
  // Veřejná stránka se vykresluje bez ohledu na přihlášení
  if (isPublicView) {
    return <PublicSite />;
  }

  // Zobrazit načítací stav během inicializace Firebase Auth
  if (loading) {
    return <div className="loading-screen">Načítání...</div>;
  }

  // Přihlašovací obrazovka má vlastní adresu; přihlášeného pustíme dál
  if (isLoginView) {
    return currentUser ? <Navigate to="/admin" replace /> : <ChessLoginScreen />;
  }

  // Administrace je jen pro přihlášené
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-6 text-sm font-medium ${
                  activeTab === tab.id
                    ? "border-b-2 border-blue-500 text-blue-600"
                    : "text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="mt-6">
          {activeTab === "tournament" && (
            <Tournament
              playerCount={playerCount}
              setPlayerCount={setPlayerCount}
              players={players}
              setPlayers={setPlayers}
              results={results}
              setResults={setResults}
              allPlayers={allPlayers}
              setAllPlayers={setAllPlayers}
              handicapSettings={handicapSettings}
              rankingMode={rankingMode}
              tournamentDate={tournamentDate}
              setTournamentDate={setTournamentDate}
              doubleRound={doubleRound}
              setDoubleRound={setDoubleRound}
            />
          )}
          {activeTab === "championship" && (
            <Championship players={allPlayers} />
          )}
          {activeTab === "players" && (
            <Players players={allPlayers} setPlayers={setAllPlayers} />
          )}
          {activeTab === "configuration" && (
            <Configuration
              playerCount={playerCount}
              setPlayerCount={setPlayerCount}
              exportData={exportData}
              importData={importData}
              handicapSettings={handicapSettings}
              setHandicapSettings={setHandicapSettings}
              rankingMode={rankingMode}
              setRankingMode={setRankingMode}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
